/**
 * Normalization: NVD prose -> speakable script.
 *
 * Pure, no I/O, no randomness, no model. This module is the most-tested part of
 * the app because most audible quality lives here. It never invents facts: it
 * only removes markup, spells identifiers, expands a small conservative set of
 * initialisms, and cuts the text to a length the synthesizer can handle.
 */

export interface CveEntry {
  id: string;
  description: string;
  published?: string;
  lastModified?: string;
  baseScore?: number;
  severity?: string;
  source?: string;
}

/**
 * How the letters C, V and E are spelled for the voice.
 *
 * MeloTTS pronounces the token "CVE" as one word ("keev"), and it collapses
 * both "C V E" and "C.V.E." back into that word before speaking: measured on
 * Workers AI, those forms came out within 0.07 s of the word version, while
 * this spelling adds the ~0.3 s that three syllables cost. Phonetic because
 * the model will not take a hint.
 */
const SPOKEN_CVE = "see vee ee";

export interface SpeakableScript {
  id: string;
  /** `CVE-2026-106931` -> `see vee ee two zero two six. one zero six nine three one.` */
  spokenId: string;
  /** Truncated, sanitized description. Terminal punctuation guaranteed. */
  description: string;
  /** Description split into synthesis units, each <= MAX_CHUNK_CHARS. */
  chunks: string[];
  /** Full text handed to a non-seekable narrator: spoken id, then description. */
  text: string;
}

export const DESCRIPTION_CAP_CHARS = 600;
export const MAX_CHUNK_CHARS = 300;

const TERMINAL = /[.!?]$/;
const TERMINAL_WITH_QUOTE = /[.!?]["')\]]$/;

/** CVSS vectors are letter soup when spoken, and NVD puts them in prose sometimes. */
const CVSS_VECTOR = /\b(?:CVSS:\d(?:\.\d)?\/)?(?:[A-Z]{1,3}:[A-Z]{1,2})(?:\/[A-Z]{1,3}:[A-Z]{1,2})+\b/g;

const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

/**
 * Step 4's initialism map. Deliberately conservative: only expansions that are
 * unambiguous and improve intelligibility. Anything not listed is spoken as
 * written rather than guessed at.
 */
const INITIALISMS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\bRCEs\b/g, "remote code executions"],
  [/\bRCE\b/g, "remote code execution"],
  [/\bXSS\b/g, "cross site scripting"],
  [/\bCSRF\b/g, "cross site request forgery"],
  [/\bSSRF\b/g, "server side request forgery"],
  [/\bSQLi\b/g, "SQL injection"],
  [/\b(?:DoS|DOS)\b/g, "denial of service"],
  [/\bLFI\b/g, "local file inclusion"],
  [/\bRFI\b/g, "remote file inclusion"],
  [/\bOOB\b/g, "out of band"],
  // An inline mention takes the same spelling as the identifier itself.
  [/\bCVE\b/g, SPOKEN_CVE],
];

/** Step 1: strip markup and machine notation. */
export function sanitize(input: string): string {
  let text = input ?? "";
  text = text.replace(/<[^>]*>/g, " ");
  for (const [entity, replacement] of Object.entries(ENTITIES)) {
    text = text.split(entity).join(replacement);
  }
  text = text.replace(/`+/g, "");
  text = text.replace(/\*\*|__/g, "");
  text = text.replace(CVSS_VECTOR, " ");
  text = text.replace(/\u00a0/g, " ");
  text = text.replace(/\s+/g, " ").trim();
  return text;
}

/** Step 2: spell the identifier so no model reads it as a number. */
export function formatCveId(id: string): string {
  const match = /^CVE-(\d{4})-(\d+)$/i.exec(id.trim());
  if (!match) return spellDigits(id);
  const year = spellDigits(match[1] ?? "");
  const rest = spellDigits(match[2] ?? "");
  return `${SPOKEN_CVE} ${year}. ${rest}.`;
}

function spellDigits(digits: string): string {
  const words = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
  return digits
    .split("")
    .map((char) => words[Number(char)] ?? char)
    .join(" ");
}

/** Step 3: expand initialisms. */
export function expandInitialisms(input: string): string {
  let text = input;
  for (const [pattern, replacement] of INITIALISMS) {
    text = text.replace(pattern, replacement);
  }
  return text;
}

/** Step 4: split into sentences, keeping terminal punctuation. */
export function segmentSentences(input: string): string[] {
  const sentences: string[] = [];
  let cursor = 0;
  const boundary = /[.!?]+["')\]]?(?=\s|$)/g;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(input)) !== null) {
    const end = match.index + match[0].length;
    const piece = input.slice(cursor, end).trim();
    if (piece) sentences.push(piece);
    cursor = end;
  }
  const tail = input.slice(cursor).trim();
  if (tail) sentences.push(tail);
  return sentences;
}

function lastSentenceBoundary(text: string, limit: number): number {
  if (limit <= 0) return -1;
  let best = -1;
  const boundary = /[.!?]+["')\]]?(?=\s|$)/g;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(text)) !== null) {
    const end = match.index + match[0].length;
    if (end > limit) break;
    best = end;
  }
  return best;
}

function lastClauseBoundary(text: string, limit: number): number {
  if (limit <= 0) return -1;
  let best = -1;
  const boundary = /[,;:\u2014\u2013](?=\s)/g;
  let match: RegExpExecArray | null;
  while ((match = boundary.exec(text)) !== null) {
    const end = match.index + match[0].length;
    if (end > limit) break;
    best = end;
  }
  return best;
}

/** Word boundary ending just after the space, so no character is lost. */
function lastWordBoundary(text: string, limit: number): number {
  if (limit <= 0) return -1;
  const space = text.lastIndexOf(" ", limit - 1);
  return space > 0 ? space + 1 : -1;
}

/** Never split a surrogate pair or a base character from its combining mark. */
export function unicodeSafeCut(text: string, limit: number): number {
  if (limit <= 0) return 0;
  let cut = Math.min(limit, text.length);
  if (cut === 0 || cut >= text.length) return cut;
  const code = text.charCodeAt(cut - 1);
  if (code >= 0xd800 && code <= 0xdbff) cut -= 1;
  if (cut > 0) {
    const next = text.charCodeAt(cut);
    if (next >= 0x0300 && next <= 0x036f) cut -= 1;
  }
  return Math.max(cut, 1);
}

function stripTrailingSeparators(text: string): string {
  return text.replace(/[\s,;:\u2014\u2013-]+$/u, "");
}

function terminate(text: string, cap: number): string {
  const trimmed = text.trim();
  if (TERMINAL.test(trimmed) || TERMINAL_WITH_QUOTE.test(trimmed)) {
    return trimmed.length <= cap ? trimmed : trimmed.slice(0, cap);
  }
  const body = stripTrailingSeparators(trimmed);
  if (body.length < cap) return `${body}.`;
  // Exactly at the cap and unterminated: keep the cap, lose one character.
  const shortened = stripTrailingSeparators(body.slice(0, Math.max(cap - 1, 0)));
  return `${shortened}.`.slice(0, cap);
}

/**
 * Step 5: truncate to the cap, inclusive, with terminal punctuation counted
 * inside the cap. Falls back sentence -> clause -> word -> hard, so the cap is
 * unconditional.
 */
export function truncateDescription(
  input: string,
  cap: number = DESCRIPTION_CAP_CHARS,
): string {
  const clean = input.trim();
  if (clean.length <= cap) return terminate(clean, cap);

  // Reserve one character for terminal punctuation appended by `terminate`.
  const bodyLimit = cap - 1;

  const sentence = lastSentenceBoundary(clean, bodyLimit);
  if (sentence > 0) return terminate(clean.slice(0, sentence), cap);

  const clause = lastClauseBoundary(clean, bodyLimit);
  if (clause > 0) return terminate(clean.slice(0, clause), cap);

  const word = lastWordBoundary(clean, bodyLimit);
  if (word > 0) return terminate(clean.slice(0, word), cap);

  return terminate(clean.slice(0, unicodeSafeCut(clean, bodyLimit)), cap);
}

/**
 * Step 6: chunk into synthesis units, falling back sentence -> clause -> word ->
 * hard split. Chunks are contiguous slices: `chunks.join("")` reconstructs the
 * input exactly, which is what makes truncation round-trips testable.
 */
export function chunkText(
  input: string,
  limit: number = MAX_CHUNK_CHARS,
): string[] {
  if (!input) return [];
  if (limit < 1) throw new RangeError("chunk limit must be positive");
  const chunks: string[] = [];
  let rest = input;

  while (rest.length > limit) {
    let cut = lastSentenceBoundary(rest, limit);
    if (cut <= 0) cut = lastClauseBoundary(rest, limit);
    if (cut <= 0) cut = lastWordBoundary(rest, limit);
    if (cut <= 0) cut = unicodeSafeCut(rest, limit);
    cut = Math.min(cut, limit);
    if (cut <= 0) cut = Math.min(limit, rest.length);
    chunks.push(rest.slice(0, cut));
    rest = rest.slice(cut);
  }

  if (rest.length > 0) chunks.push(rest);
  return chunks;
}

/** Full pipeline. The order is load-bearing; see the plan's Phase 3. */
export function normalizeCve(entry: CveEntry): SpeakableScript {
  const spokenId = formatCveId(entry.id);
  const description = truncateDescription(
    expandInitialisms(sanitize(entry.description ?? "")),
    DESCRIPTION_CAP_CHARS,
  );
  const chunks = chunkText(description, MAX_CHUNK_CHARS);
  return {
    id: entry.id,
    spokenId,
    description,
    chunks,
    text: `${spokenId} ${description}`.trim(),
  };
}
